import jsPDF from "jspdf";

const money = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function loadLogo(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function exportAnnualPDF(data, year, macOverride) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 14;
  let y = 14;
  const logo = await loadLogo("/blu-lodge-logo.png");
  if (logo) doc.addImage(logo, "PNG", M, y - 2, 18, 18);
  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(15, 23, 42);
  doc.text(`Resoconto Annuale ${year}`, M + 22, y + 4);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(100, 116, 139);
  doc.text("Blu Lodge Maldives", M + 22, y + 10);
  y += 22;

  const line = () => { doc.setDrawColor(226, 232, 240); doc.line(M, y, W - M, y); y += 1; };
  const check = (h = 8) => { if (y + h > 285) { doc.addPage(); y = 16; } };
  const sectionTitle = (t) => { check(12); doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(15, 23, 42); doc.text(t, M, y); y += 5; line(); y += 2; };
  const row = (cols, opts = {}) => {
    check();
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(opts.size || 9);
    doc.setTextColor(...(opts.color || [51, 65, 85]));
    cols.forEach((c) => doc.text(String(c.t), c.x, y, { align: c.align || "left" }));
    y += opts.gap || 5;
  };

  const s = data.spese;
  // KPI
  sectionTitle("Sintesi");
  row([{ t: "Arrivi", x: M }, { t: data.arrivi, x: 60, align: "right" }, { t: "Entrate", x: 120 }, { t: money(data.entrate), x: W - M, align: "right" }]);
  row([{ t: "Partenze", x: M }, { t: data.partenze, x: 60, align: "right" }, { t: "Netto", x: 120 }, { t: money(data.netto), x: W - M, align: "right" }]);
  row([{ t: "Prenotazioni", x: M }, { t: data.bookings_count ?? "-", x: 60, align: "right" }, { t: "Cash incassato", x: 120 }, { t: money(data.cash_collected), x: W - M, align: "right" }]);
  y += 3;

  // Spese
  sectionTitle("Riepilogo spese");
  const sr = (label, val, indent = false, bold = false) => row([{ t: (indent ? "   - " : "") + label, x: indent ? M + 4 : M }, { t: money(val), x: W - M, align: "right" }], { bold, color: indent ? [100, 116, 139] : [51, 65, 85] });
  sr("Maldive Autentiche", s.maldive_autentiche);
  Object.entries(s.manual || {}).forEach(([k, v]) => sr(k, v));
  sr("Affitti", s.affitti);
  sr("Staff", s.staff, false, true);
  if (data.staff_breakdown) { sr("Stipendi", data.staff_breakdown.salaries, true); sr("Bonus", data.staff_breakdown.bonuses, true); }
  sr("Spese Guesthouse", s.guesthouse.total, false, true);
  sr("Amministrazione", (s.amministrazione?.total) || 0, false, true);
  sr("Costi altre strutture", s.keyodhoo_costi);
  sr("MIRA", s.mira);
  y += 1; line(); y += 2;
  sr("Totale spese", s.total, false, true);
  y += 3;

  // Commissioni MA
  const mac = macOverride || data.ma_commissions;
  if (mac) {
    sectionTitle(`Commissioni versate a Maldive Autentiche — Totale ${money(mac.total)}`);
    const cx = [M, 78, 104, 130, 156, W - M];
    row([{ t: "Agenzia", x: cx[0] }, { t: "13+", x: cx[1], align: "right" }, { t: "5-12", x: cx[2], align: "right" }, { t: "2-4", x: cx[3], align: "right" }, { t: "0-1", x: cx[4], align: "right" }, { t: "Versato", x: cx[5], align: "right" }], { bold: true, size: 8, color: [100, 116, 139] });
    mac.agencies.forEach((a) => row([{ t: a.name, x: cx[0] }, { t: a.counts.a, x: cx[1], align: "right" }, { t: a.counts.c, x: cx[2], align: "right" }, { t: a.counts.c2, x: cx[3], align: "right" }, { t: a.counts.inf, x: cx[4], align: "right" }, { t: money(a.total), x: cx[5], align: "right" }], { size: 8 }));
    row([{ t: "Totale generale", x: cx[0] }, { t: mac.counts.a, x: cx[1], align: "right" }, { t: mac.counts.c, x: cx[2], align: "right" }, { t: mac.counts.c2, x: cx[3], align: "right" }, { t: mac.counts.inf, x: cx[4], align: "right" }, { t: money(mac.total), x: cx[5], align: "right" }], { bold: true, size: 8 });
    y += 3;
  }

  // Costi altre strutture
  const pc = data.property_costs;
  if (pc) {
    sectionTitle(`Costi altre strutture (pagati da Blu Lodge) — Totale ${money(pc.total)}`);
    const cx = [M, 120, 150, W - M];
    row([{ t: "Struttura", x: cx[0] }, { t: "Pren.", x: cx[1], align: "right" }, { t: "Notti", x: cx[2], align: "right" }, { t: "Costo", x: cx[3], align: "right" }], { bold: true, size: 8, color: [100, 116, 139] });
    pc.structures.forEach((st) => row([{ t: st.name, x: cx[0] }, { t: st.bookings, x: cx[1], align: "right" }, { t: st.nights, x: cx[2], align: "right" }, { t: money(st.total), x: cx[3], align: "right" }], { size: 8 }));
    if (pc.structures.length === 0) row([{ t: "Nessun costo registrato", x: M, color: [148, 163, 184] }], { size: 8 });
    y += 3;
  }

  // Prenotazioni per origine
  if (data.per_agency) {
    sectionTitle("Prenotazioni per origine");
    const cx = [M, 110, 150, W - M];
    row([{ t: "Origine", x: cx[0] }, { t: "Pren.", x: cx[1], align: "right" }, { t: "Entrate", x: cx[2], align: "right" }, { t: "Commissione", x: cx[3], align: "right" }], { bold: true, size: 8, color: [100, 116, 139] });
    Object.entries(data.per_agency).filter(([, v]) => v.count > 0).forEach(([k, v]) => row([{ t: k, x: cx[0] }, { t: v.count, x: cx[1], align: "right" }, { t: money(v.entrate), x: cx[2], align: "right" }, { t: money(v.commissione), x: cx[3], align: "right" }], { size: 8 }));
  }

  doc.save(`Annuale_${year}_BluLodge.pdf`);
}
